import {RenderAlert} from "components/src/common/RenderAlert";
import {SaveChangesRef} from "components/src/common/SaveChangesRef";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {useCallback, useRef, useState} from 'react'
import Button from 'react-bootstrap/Button';
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import {useLocation} from "react-router-dom";
import {SettingsLevelAndSubLevel, SettingsUtils} from "./SettingsUtils";
import {SettingsView} from "./SettingsView";

/**
 * Show the user settings on system level. Which settings are edited is determined by the URL path segments.
 *
 * The path params have the following structure
 *
 * path :== /system/<category>
 *
 * <category> :== <category-name> | <category-name>/<category>
 * <category-name> :== <letter> | <letter><category-name>
 *
 * On smaller screens, the menu will be shown as an off canvas popup
 */
export function Settings()
{
    /**
     * The path segments have the following structure
     *
     * path :== /system/<category>
     *
     * <category> :== <category-name> | <category-name>/<category>
     * <category-name> :== <letter> | <letter><category-name>
     */
    const location = useLocation()
    /**
     * A reference to the SettingsView component which is needed to save the settings when the save button is clicked
     */
    const saveChangesRef = useRef<SaveChangesRef>();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * If set, items should be saved
     */
    const [shouldSave, setShouldSave] = useState<boolean>(false);
    /**
     * Set to true while saving to show a spinner on the download button
     */
    const [saving, setSaving] = useState<boolean>(false);

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * Keeps track of the setting level and setting category (this is based on the URL)
     * Extracts the path from the params and splits the parts into a string array.
     *
     * Note: Access to the RAW URL parameters is required because an email address can contain a / character.
     * We therefore cannot use useParams() because this contains the URL decoded parameters
     */
    const getSettingsLevelAndSubLevel = useCallback(() : SettingsLevelAndSubLevel =>
    {
        const path = location.pathname
            .split("/")
            .map(s => decodeURIComponent(s))
            .filter(s => s.length > 0);

        // we need to remove the /system prefix
        path.shift();

        return SettingsUtils.getSettingsLevelAndSubLevel(path);
    }, [location.pathname]);

    const settingsLevelAndSubLevel = getSettingsLevelAndSubLevel();

    return (
        <>
            <PageContent className="flex-grow-1 min-h-0">
                <Row className="h-100 w-100">
                    <SettingsView settingsLevel={settingsLevelAndSubLevel.settingsLevel}
                                  settingsLevelSubSelector={settingsLevelAndSubLevel.settingsLevelSubSelector}
                                  categories={settingsLevelAndSubLevel.categories.join("/")}
                                  setErrorMessage={setErrorMessage}
                                  setShouldSave={setShouldSave}
                                  ref={saveChangesRef}
                                  setSaving={setSaving}
                    />
                </Row>
            </PageContent>

            <Row>
                <div className="d-flex justify-content-end align-items-baseline mt-4 p-4 border-top">
                    {/* show an alert if a property cannot be loaded */}
                    <RenderAlert message={errorMessage}/>

                    <Button variant="success" type="submit"
                            onClick={() => saveChangesRef.current?.saveChanges()}
                            disabled={!shouldSave}>
                        {saving && <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            aria-hidden="true"
                            className="me-1"
                        />}
                        {saving ? t("common.saving") : t("common.save")}
                    </Button>
                </div>
            </Row>
        </>
    )
}
